/*
 * Footer Newsletter — vanilla submit handler for the Klaviyo signup
 * form rendered by blocks/_footer-newsletter.liquid. Mirrors the
 * assets/newsletter-popup.js pattern: scoped binding via the block's
 * [data-footer-newsletter] root, plain fetch to the form's
 * data-ajax-submit endpoint (Klaviyo kmail subscribe AJAX), and a
 * native-action fallback so the signup is never silently lost when
 * the AJAX call is hostile (CORS / non-JSON).
 *
 *   Submit   POST the form data to the data-ajax-submit endpoint.
 *            On success: add `newsletter-success` to the root — the
 *            block's stylesheet hides the form and reveals the success
 *            block. On error: put the message in `.error_message` and
 *            reveal `.klaviyo_messages` via the `.has-message` class.
 *            On network/CORS failure: fall back to the form's native
 *            action (target=_blank kmail page).
 *
 * Loaded globally from snippets/scripts.liquid; re-binds each
 * [data-footer-newsletter] root on load and on shopify:section:load.
 */

const SELECTOR = '[data-footer-newsletter]';
const INSTANCE_KEY = '_footerNewsletterInstance';

class FooterNewsletter {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form.newsletter-klaviyo-form');
    this.messagesEl = root.querySelector('.klaviyo_messages');
    this.errorEl = root.querySelector('.error_message');

    if (!this.form) return;

    this.init();
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.root[INSTANCE_KEY] = this;
  }

  succeed() {
    this.root.classList.add('newsletter-success');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || 'An error occurred. Please try again.';
    if (this.messagesEl) this.messagesEl.classList.add('has-message');
  }

  handleSubmit(event) {
    if (!this.form || !this.form.dataset.ajaxSubmit) return;

    event.preventDefault();

    if (this.errorEl) this.errorEl.textContent = '';
    if (this.messagesEl) this.messagesEl.classList.remove('has-message');

    let endpoint = this.form.dataset.ajaxSubmit;
    if (endpoint.startsWith('//')) endpoint = `https:${endpoint}`;

    const params = new URLSearchParams(new FormData(this.form));
    // Klaviyo expects the non-email profile fields listed in $fields.
    params.set('$fields', 'first_name,last_name,Signup Source');

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-cache',
      },
      body: params.toString(),
    })
      .then((response) => response.json())
      .then((data) => {
        const ok = data && (data.success === true || (data.success === undefined && !data.errors));
        if (ok) {
          this.succeed();
        } else {
          const errors = data && data.errors;
          this.showError(Array.isArray(errors) ? errors[0] : errors);
        }
      })
      .catch(() => {
        this.form.submit();
      });
  }

  destroy() {
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new FooterNewsletter(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
